import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, MapPin } from "lucide-react";
import { LOCATION, PDF_BG, EVENT } from "../data/content";
import SectionBg from "./SectionBg";

export default function Location() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [-100, 100]);

    return (
        <section
            id="location"
            ref={ref}
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="location-section"
        >
            <SectionBg src={PDF_BG.location} opacity={0.42} />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
                    {/* Image side */}
                    <div className="col-span-12 lg:col-span-6 min-w-0 relative overflow-hidden rounded-3xl">
                        <motion.div
                            style={{ y }}
                            className="relative aspect-[4/5] max-h-[480px] rounded-3xl overflow-hidden border border-white/10 sm:max-h-none max-[639px]:!transform-none"
                        >
                            <img
                                src={PDF_BG.location}
                                alt="Jaipur"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
                                {/* <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono mb-3">
                                    <MapPin size={14} /> {EVENT.venue}
                                </div> */}
                                <div className="max-w-full font-display text-[1.45rem] leading-tight font-bold break-words [overflow-wrap:anywhere] sm:text-3xl md:text-4xl">Jaipur, Rajasthan</div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Content side */}
                    <div className="col-span-12 lg:col-span-6 min-w-0">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono mb-6">
                            {LOCATION.eyebrow}
                        </div>
                        <h2 className="min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl mb-6 md:mb-8">
                            {LOCATION.title}
                        </h2>
                        <p className="text-zinc-300 leading-relaxed text-sm sm:text-base md:text-lg mb-8 md:mb-10 [overflow-wrap:anywhere]">
                            {LOCATION.body}
                        </p>

                        <ul className="space-y-3">
                            {LOCATION.points.map((point, i) => (
                                <motion.li
                                    key={point}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.06 }}
                                    className="flex items-start gap-3 group"
                                    data-testid={`location-point-${i}`}
                                >
                                    <span className="flex-none w-6 h-6 rounded-full bg-[#FF6B35]/15 flex items-center justify-center text-[#FF6B35] mt-0.5 group-hover:bg-[#FF6B35] group-hover:text-black transition">
                                        <Check size={12} strokeWidth={3} />
                                    </span>
                                    <span className="min-w-0 text-zinc-300 leading-relaxed [overflow-wrap:anywhere]">{point}</span>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}
